import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  CreateUpstreamModelMappingRequest,
  CreateUpstreamRequest,
  ProbeResponse,
  UpstreamConfig,
  UpstreamProtocol,
  UpstreamVisibility,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { useChannelsList } from '../api/useChannels.ts';
import { useModelsList, useModelProtocols } from '../api/useModels.ts';
import { useCreateModelMapping } from '../api/useModelMappings.ts';
import { useCreateKeyBucket } from '../api/useKeyBuckets.ts';
import { useCreateUpstreamAccount } from '../api/useUpstreamAccounts.ts';
import {
  useCreateUpstream,
  useProbeUpstream,
  useUpdateUpstream,
} from '../api/useUpstreams.ts';
import { ProbeResultPanel } from '../components/ProbeResultPanel.tsx';
import { coerceErrorMessage, useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { INPUT_CLASS } from '../styles/forms.ts';

type StepId = 1 | 2 | 3 | 4;

interface BasicDraft {
  readonly name: string;
  readonly endpointUrl: string;
  readonly priority: string;
  readonly visibility: UpstreamVisibility;
  readonly allowCookiePassthrough: boolean;
}

const EMPTY_BASIC: BasicDraft = {
  name: '',
  endpointUrl: '',
  priority: '100',
  visibility: 'all',
  allowCookiePassthrough: false,
};

const VISIBILITY_OPTIONS: readonly UpstreamVisibility[] = ['all', 'allowlist'];

const STEP_LABEL_KEYS: Record<StepId, I18nKey> = {
  1: 'upstream_wizard.step.basic.title',
  2: 'upstream_wizard.step.keys.title',
  3: 'upstream_wizard.step.models.title',
  4: 'upstream_wizard.step.probe.title',
};

function parsePositiveIntOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

const UPSTREAM_PROTOCOLS: readonly UpstreamProtocol[] = ['anthropic', 'openai'];

function isUpstreamProtocol(value: string): value is UpstreamProtocol {
  return (UPSTREAM_PROTOCOLS as readonly string[]).includes(value);
}

function isVisibility(value: string): value is UpstreamVisibility {
  return (VISIBILITY_OPTIONS as readonly string[]).includes(value);
}

function coerceMessage(err: unknown): string {
  if (err instanceof OpsApiError) return `${err.code}: ${err.message}`;
  return coerceErrorMessage(err);
}

interface MappingEntry {
  readonly vendor: string;
  readonly channel: string;
  readonly model: string;
  readonly protocol: string;
}

export function NewUpstreamWizard(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();

  const [step, setStep] = useState<StepId>(1);
  const [upstream, setUpstream] = useState<UpstreamConfig | null>(null);
  const [basic, setBasic] = useState<BasicDraft>(EMPTY_BASIC);
  const [keyBucketCount, setKeyBucketCount] = useState(0);
  const [mappings, setMappings] = useState<readonly MappingEntry[]>([]);
  const [probeResult, setProbeResult] = useState<ProbeResponse | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);

  const upstreamId = upstream?.id ?? '';
  const createMutation = useCreateUpstream();
  const probeMutation = useProbeUpstream(upstreamId);
  const updateMutation = useUpdateUpstream(upstreamId);
  const [finishing, setFinishing] = useState<boolean>(false);

  return (
    <section data-testid="upstream-wizard-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-3 text-[12px] text-outline">
          <Link
            to="/supply/upstreams"
            data-testid="upstream-wizard-breadcrumb"
            className="hover:text-on-surface focus-visible:outline-none focus-visible:underline"
          >
            {t('upstream_wizard.breadcrumb.upstreams')}
          </Link>
          <span aria-hidden="true" className="text-outline-variant">/</span>
          <span className="text-on-surface-variant">{t('upstream_wizard.breadcrumb.new')}</span>
        </div>
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('upstream_wizard.title')}
        </h2>
        <p className="text-sm text-outline">{t('upstream_wizard.subtitle')}</p>
      </header>

      <ol
        aria-label={t('upstream_wizard.progress.aria_label')}
        data-testid="upstream-wizard-progress"
        className="flex flex-wrap items-center gap-2 rounded-xl bg-surface-container-low p-2"
      >
        {([1, 2, 3, 4] as const).map((id) => {
          const active = id === step;
          const done = id < step;
          const label = t(STEP_LABEL_KEYS[id]);
          const cls = active
            ? 'bg-primary-container text-on-primary'
            : done
            ? 'bg-tertiary-container text-on-tertiary-container'
            : 'bg-surface-container-lowest text-outline';
          return (
            <li
              key={id}
              aria-current={active ? 'step' : undefined}
              data-testid={`upstream-wizard-progress-${id}`}
              data-active={active ? 'true' : 'false'}
              data-done={done ? 'true' : 'false'}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-[11px] font-bold uppercase tracking-[0.08em] ${cls}`}
            >
              <span aria-hidden="true" className="font-mono">{done ? '✓' : id}</span>
              <span>{label}</span>
            </li>
          );
        })}
      </ol>

      {step === 1 ? (
        <Step1Basic
          draft={basic}
          setDraft={setBasic}
          submitting={createMutation.isPending}
          error={stepError}
          onSubmit={async (e) => {
            e.preventDefault();
            setStepError(null);
            const name = basic.name.trim();
            const endpointUrl = basic.endpointUrl.trim();
            const priority = parsePositiveIntOrNull(basic.priority);
            if (name.length === 0 || endpointUrl.length === 0) {
              setStepError(t('upstream_wizard.step.basic.error.required'));
              return;
            }
            if (priority === null) {
              setStepError(t('upstream_wizard.step.basic.error.priority'));
              return;
            }
            const body: CreateUpstreamRequest = {
              name,
              kind: 'forward',
              endpoint_url: endpointUrl,
              enabled: false,
              priority,
              tenant_visibility: basic.visibility,
              allow_cookie_passthrough: basic.allowCookiePassthrough,
            };
            try {
              const created = await createMutation.mutateAsync(body);
              setUpstream(created);
              setStep(2);
              toast.success(t('upstream_wizard.toast.created', { name: created.name }));
            } catch (err) {
              const message = coerceMessage(err);
              setStepError(message);
              toast.error(message);
            }
          }}
        />
      ) : null}

      {step === 2 && upstream !== null ? (
        <Step2KeyBuckets
          upstreamId={upstream.id}
          upstreamName={upstream.name}
          keyBucketCount={keyBucketCount}
          error={stepError}
          onAdded={() => {
            setKeyBucketCount((c) => c + 1);
            setStepError(null);
          }}
          onBack={() => {
            setStepError(null);
            setStep(1);
          }}
          onNext={() => {
            if (keyBucketCount === 0) {
              setStepError(t('upstream_wizard.step.keys.error.min_one'));
              return;
            }
            setStepError(null);
            setStep(3);
          }}
        />
      ) : null}

      {step === 3 && upstream !== null ? (
        <Step3ConnectModels
          upstreamId={upstream.id}
          upstreamName={upstream.name}
          mappings={mappings}
          error={stepError}
          onAdded={(entry) => {
            setMappings((prev) => [...prev, entry]);
            setStepError(null);
          }}
          onBack={() => {
            setStepError(null);
            setStep(2);
          }}
          onNext={() => {
            if (mappings.length === 0) {
              setStepError(t('upstream_wizard.step.models.error.min_one'));
              return;
            }
            setStepError(null);
            setStep(4);
          }}
        />
      ) : null}

      {step === 4 && upstream !== null ? (
        <Step4Probe
          upstreamId={upstream.id}
          upstreamName={upstream.name}
          mappings={mappings}
          probeResult={probeResult}
          probing={probeMutation.isPending}
          finishing={finishing}
          error={stepError}
          onProbe={async (entry) => {
            setStepError(null);
            try {
              const res = await probeMutation.mutateAsync({
                body: {
                  vendor: entry.vendor,
                  model: entry.model,
                  protocol: isUpstreamProtocol(entry.protocol) ? entry.protocol : undefined,
                },
              });
              setProbeResult(res);
            } catch (err) {
              const message = coerceMessage(err);
              setStepError(message);
              toast.error(message);
            }
          }}
          onBack={() => {
            setStepError(null);
            setStep(3);
          }}
          onFinish={async () => {
            setFinishing(true);
            setStepError(null);
            try {
              await updateMutation.mutateAsync({ enabled: true });
              toast.success(t('upstream_wizard.toast.enabled', { name: upstream.name }));
              navigate(`/supply/upstreams/${upstream.id}`);
            } catch (err) {
              const message = coerceMessage(err);
              setStepError(message);
              toast.error(message);
              setFinishing(false);
            }
          }}
        />
      ) : null}
    </section>
  );
}

// ─── Step 1: Basic Configuration ────────────────────────────────────────

interface Step1Props {
  readonly draft: BasicDraft;
  readonly setDraft: (d: BasicDraft) => void;
  readonly submitting: boolean;
  readonly error: string | null;
  readonly onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

function Step1Basic({ draft, setDraft, submitting, error, onSubmit }: Step1Props): JSX.Element {
  const t = useT();
  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="upstream-wizard-step-1"
      className="flex flex-col gap-5 rounded-xl bg-surface-container p-6"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-on-surface">
          {t('upstream_wizard.step.basic.heading')}
        </h3>
        <p className="text-sm text-outline">{t('upstream_wizard.step.basic.body')}</p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_wizard.step.basic.name')}
            <span className="ml-1 text-primary">*</span>
          </span>
          <input
            type="text"
            required
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })}
            placeholder={t('upstream_wizard.step.basic.name.placeholder')}
            data-testid="upstream-wizard-field-name"
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_wizard.step.basic.endpoint')}
            <span className="ml-1 text-primary">*</span>
          </span>
          <input
            type="url"
            required
            value={draft.endpointUrl}
            onChange={(e) => setDraft({ ...draft, endpointUrl: e.currentTarget.value })}
            placeholder={t('upstream_wizard.step.basic.endpoint.placeholder')}
            data-testid="upstream-wizard-field-endpoint"
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_wizard.step.basic.priority')}
          </span>
          <input
            type="number"
            min={0}
            step={1}
            value={draft.priority}
            onChange={(e) => setDraft({ ...draft, priority: e.currentTarget.value })}
            data-testid="upstream-wizard-field-priority"
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_wizard.step.basic.visibility')}
          </span>
          <select
            value={draft.visibility}
            onChange={(e) => {
              const next = e.currentTarget.value;
              if (isVisibility(next)) setDraft({ ...draft, visibility: next });
            }}
            data-testid="upstream-wizard-field-visibility"
            className={INPUT_CLASS}
          >
            {VISIBILITY_OPTIONS.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-2 rounded-lg bg-surface-container-low p-4 md:col-span-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_wizard.step.basic.cookie_passthrough')}
          </span>
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={draft.allowCookiePassthrough}
              onChange={(e) =>
                setDraft({ ...draft, allowCookiePassthrough: e.currentTarget.checked })
              }
              data-testid="upstream-wizard-field-cookie-passthrough"
              className="mt-1 h-4 w-4 shrink-0 accent-primary"
            />
            <div className="flex flex-col gap-1">
              <span className="text-sm text-on-surface">
                {t('upstream_wizard.step.basic.cookie_passthrough.body')}
              </span>
              <span className="text-xs text-outline">
                {t('upstream_wizard.step.basic.cookie_passthrough.hint')}
              </span>
            </div>
          </div>
        </label>
      </div>
      {error !== null ? (
        <p role="alert" data-testid="upstream-wizard-error" className="text-xs text-error">
          {error}
        </p>
      ) : null}
      <footer className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={submitting}
          data-testid="upstream-wizard-step-1-submit"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
        >
          {submitting
            ? t('upstream_wizard.step.basic.submit.pending')
            : t('upstream_wizard.step.basic.submit')}
        </button>
      </footer>
    </form>
  );
}

// ─── Step 2: Key Buckets ────────────────────────────────────────────────

interface Step2Props {
  readonly upstreamId: string;
  readonly upstreamName: string;
  readonly keyBucketCount: number;
  readonly error: string | null;
  readonly onAdded: () => void;
  readonly onBack: () => void;
  readonly onNext: () => void;
}

function Step2KeyBuckets({
  upstreamId,
  upstreamName,
  keyBucketCount,
  error,
  onAdded,
  onBack,
  onNext,
}: Step2Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const createAccount = useCreateUpstreamAccount(upstreamId);
  const createBucket = useCreateKeyBucket(upstreamId);
  const [defaultAccountId, setDefaultAccountId] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [secret, setSecret] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  async function ensureDefaultAccount(): Promise<string> {
    if (defaultAccountId !== null) return defaultAccountId;
    const res = await createAccount.mutateAsync({ label: 'Default' });
    setDefaultAccountId(res.account.id);
    return res.account.id;
  }

  async function handleAdd(): Promise<void> {
    setAddError(null);
    const secretTrimmed = secret.trim();
    if (secretTrimmed.length === 0) {
      setAddError(t('upstream_wizard.step.keys.error.secret_required'));
      return;
    }
    try {
      const accountId = await ensureDefaultAccount();
      await createBucket.mutateAsync({
        account_id: accountId,
        label: label.trim() || undefined,
        auth_secret_ref: secretTrimmed,
      });
      toast.success(t('upstream_wizard.step.keys.toast.added'));
      onAdded();
      setLabel('');
      setSecret('');
    } catch (err) {
      const message = coerceMessage(err);
      setAddError(message);
      toast.error(message);
    }
  }

  return (
    <div
      data-testid="upstream-wizard-step-2"
      className="flex flex-col gap-5 rounded-xl bg-surface-container p-6"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-on-surface">
          {t('upstream_wizard.step.keys.heading', { name: upstreamName })}
        </h3>
        <p className="text-sm text-outline">{t('upstream_wizard.step.keys.body')}</p>
      </div>

      <div className="flex items-center gap-2 rounded-lg bg-surface-container-low p-3">
        <span className="text-sm text-on-surface-variant">
          {t('upstream_wizard.step.keys.count', { count: String(keyBucketCount) })}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 rounded-lg bg-surface-container-low p-4 md:grid-cols-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_wizard.step.keys.label')}
          </span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
            placeholder={t('upstream_wizard.step.keys.label.placeholder')}
            data-testid="upstream-wizard-kb-label"
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            API Key <span className="text-primary">*</span>
          </span>
          <input
            type="password"
            autoComplete="off"
            value={secret}
            onChange={(e) => setSecret(e.currentTarget.value)}
            placeholder="sk-..."
            data-testid="upstream-wizard-kb-secret"
            className={INPUT_CLASS}
          />
        </label>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={createBucket.isPending}
          data-testid="upstream-wizard-kb-add"
          className="inline-flex items-center gap-1 rounded-lg bg-primary-container px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-primary transition-colors hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[16px]">add</span>
          {t('upstream_wizard.step.keys.add')}
        </button>
      </div>

      {addError !== null ? (
        <p role="alert" className="text-xs text-error">{addError}</p>
      ) : null}
      {error !== null ? (
        <p role="alert" data-testid="upstream-wizard-error" className="text-xs text-error">
          {error}
        </p>
      ) : null}

      <footer className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          data-testid="upstream-wizard-step-2-back"
          className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
        >
          {t('upstream_wizard.back')}
        </button>
        <button
          type="button"
          onClick={onNext}
          data-testid="upstream-wizard-step-2-next"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)]"
        >
          {t('upstream_wizard.next')}
        </button>
      </footer>
    </div>
  );
}

// ─── Step 3: Connect Models (cascading dropdowns) ───────────────────────

interface Step3Props {
  readonly upstreamId: string;
  readonly upstreamName: string;
  readonly mappings: readonly MappingEntry[];
  readonly error: string | null;
  readonly onAdded: (entry: MappingEntry) => void;
  readonly onBack: () => void;
  readonly onNext: () => void;
}

function Step3ConnectModels({
  upstreamId,
  upstreamName,
  mappings,
  error,
  onAdded,
  onBack,
  onNext,
}: Step3Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const createMapping = useCreateModelMapping(upstreamId);
  const channels = useChannelsList();
  const models = useModelsList();

  const [vendorSel, setVendorSel] = useState('');
  const [channelSel, setChannelSel] = useState('');
  const [modelSel, setModelSel] = useState('');
  const [protocolSel, setProtocolSel] = useState('');
  const [upstreamModelName, setUpstreamModelName] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const protocols = useModelProtocols(vendorSel, modelSel);

  const uniqueVendors = useMemo(() => {
    const chs = channels.data ?? [];
    const vs = new Set<string>();
    for (const ch of chs) vs.add(ch.vendor);
    return [...vs].sort();
  }, [channels.data]);

  const filteredChannels = useMemo(() => {
    if (vendorSel.length === 0) return [];
    return (channels.data ?? []).filter((ch) => ch.vendor === vendorSel);
  }, [channels.data, vendorSel]);

  const filteredModels = useMemo(() => {
    if (vendorSel.length === 0) return [];
    return (models.data ?? []).filter(
      (m) => m.vendor === vendorSel && m.deleted_at === null && m.shelf_status === 'listed',
    );
  }, [models.data, vendorSel]);

  useEffect(() => {
    setChannelSel('');
    setModelSel('');
    setProtocolSel('');
    setUpstreamModelName('');
  }, [vendorSel]);

  useEffect(() => {
    setProtocolSel('');
  }, [modelSel]);

  useEffect(() => {
    if (modelSel.length > 0 && upstreamModelName.length === 0) {
      setUpstreamModelName(modelSel);
    }
  }, [modelSel, upstreamModelName.length]);

  useEffect(() => {
    const prots = protocols.data ?? [];
    if (prots.length === 1 && prots[0] !== undefined) {
      setProtocolSel(prots[0].protocol);
    }
  }, [protocols.data]);

  const canAdd =
    vendorSel.length > 0 &&
    channelSel.length > 0 &&
    modelSel.length > 0 &&
    protocolSel.length > 0 &&
    upstreamModelName.trim().length > 0 &&
    !createMapping.isPending;

  async function handleAdd(): Promise<void> {
    setAddError(null);
    const body: CreateUpstreamModelMappingRequest = {
      vendor: vendorSel,
      channel: channelSel,
      model: modelSel,
      protocol: protocolSel,
      upstream_model_name: upstreamModelName.trim(),
    };
    try {
      await createMapping.mutateAsync(body);
      toast.success(t('upstream_wizard.step.models.toast.added'));
      onAdded({ vendor: vendorSel, channel: channelSel, model: modelSel, protocol: protocolSel });
      setModelSel('');
      setProtocolSel('');
      setUpstreamModelName('');
    } catch (err) {
      const message = coerceMessage(err);
      setAddError(message);
      toast.error(message);
    }
  }

  return (
    <div
      data-testid="upstream-wizard-step-3"
      className="flex flex-col gap-5 rounded-xl bg-surface-container p-6"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-on-surface">
          {t('upstream_wizard.step.models.heading', { name: upstreamName })}
        </h3>
        <p className="text-sm text-outline">{t('upstream_wizard.step.models.body')}</p>
      </div>

      {mappings.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {mappings.map((m) => (
            <span
              key={`${m.vendor}/${m.channel}/${m.model}/${m.protocol}`}
              className="inline-flex items-center gap-1 rounded-full bg-surface-container-highest px-3 py-1 text-[11px] text-on-surface"
            >
              <span className="text-outline-variant">{m.vendor}/</span>
              <span className="font-semibold">{m.model}</span>
              <span className="text-primary">({m.channel})</span>
            </span>
          ))}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-4 rounded-lg bg-surface-container-low p-4">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('mappings.col.vendor')} <span className="text-primary">*</span>
          </span>
          <select
            value={vendorSel}
            onChange={(e) => setVendorSel(e.currentTarget.value)}
            data-testid="upstream-wizard-mapping-vendor"
            className={INPUT_CLASS}
          >
            <option value="">{t('mappings.add_modal.select_vendor')}</option>
            {uniqueVendors.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('mappings.col.channel')} <span className="text-primary">*</span>
          </span>
          <select
            value={channelSel}
            onChange={(e) => setChannelSel(e.currentTarget.value)}
            disabled={vendorSel.length === 0}
            data-testid="upstream-wizard-mapping-channel"
            className={INPUT_CLASS}
          >
            <option value="">{t('mappings.add_modal.select_channel')}</option>
            {filteredChannels.map((ch) => (
              <option key={ch.slug} value={ch.slug}>{ch.display_name} ({ch.slug})</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('mappings.col.model')} <span className="text-primary">*</span>
          </span>
          <select
            value={modelSel}
            onChange={(e) => {
              setModelSel(e.currentTarget.value);
              setUpstreamModelName(e.currentTarget.value);
            }}
            disabled={vendorSel.length === 0}
            data-testid="upstream-wizard-mapping-model"
            className={INPUT_CLASS}
          >
            <option value="">{t('mappings.add_modal.select_model')}</option>
            {filteredModels.map((m) => (
              <option key={m.model} value={m.model}>{m.display_name ?? m.model}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            Protocol <span className="text-primary">*</span>
          </span>
          <select
            value={protocolSel}
            onChange={(e) => setProtocolSel(e.currentTarget.value)}
            disabled={modelSel.length === 0}
            data-testid="upstream-wizard-mapping-protocol"
            className={INPUT_CLASS}
          >
            <option value="">{t('mappings.add_modal.select_protocol')}</option>
            {(protocols.data ?? []).map((p) => (
              <option key={p.protocol} value={p.protocol}>{p.protocol}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 md:col-span-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('mappings.col.upstream_name')}
          </span>
          <input
            type="text"
            value={upstreamModelName}
            onChange={(e) => setUpstreamModelName(e.currentTarget.value)}
            data-testid="upstream-wizard-mapping-upstream-name"
            className={INPUT_CLASS}
          />
        </label>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={!canAdd}
          data-testid="upstream-wizard-mapping-add"
          className="inline-flex items-center gap-1 rounded-lg bg-primary-container px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-primary transition-colors hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
        >
          <span aria-hidden="true" className="material-symbols-outlined text-[16px]">add</span>
          {t('upstream_wizard.step.models.add')}
        </button>
      </div>

      {addError !== null ? (
        <p role="alert" className="text-xs text-error">{addError}</p>
      ) : null}
      {error !== null ? (
        <p role="alert" data-testid="upstream-wizard-error" className="text-xs text-error">
          {error}
        </p>
      ) : null}

      <footer className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          data-testid="upstream-wizard-step-3-back"
          className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright"
        >
          {t('upstream_wizard.back')}
        </button>
        <button
          type="button"
          onClick={onNext}
          data-testid="upstream-wizard-step-3-next"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)]"
        >
          {t('upstream_wizard.next')}
        </button>
      </footer>
    </div>
  );
}

// ─── Step 4: Probe & Enable ─────────────────────────────────────────────

interface Step4Props {
  readonly upstreamId: string;
  readonly upstreamName: string;
  readonly mappings: readonly MappingEntry[];
  readonly probeResult: ProbeResponse | null;
  readonly probing: boolean;
  readonly finishing: boolean;
  readonly error: string | null;
  readonly onProbe: (entry: MappingEntry) => Promise<void>;
  readonly onBack: () => void;
  readonly onFinish: () => Promise<void>;
}

function Step4Probe({
  upstreamId,
  upstreamName,
  mappings,
  probeResult,
  probing,
  finishing,
  error,
  onProbe,
  onBack,
  onFinish,
}: Step4Props): JSX.Element {
  const t = useT();
  const [selected, setSelected] = useState<string>(
    mappings[0] !== undefined ? `${mappings[0].vendor}/${mappings[0].model}/${mappings[0].protocol}` : '',
  );

  const target = useMemo(
    () =>
      mappings.find(
        (a) => `${a.vendor}/${a.model}/${a.protocol}` === selected,
      ) ?? null,
    [mappings, selected],
  );

  async function handleProbeClick(): Promise<void> {
    if (target === null) return;
    await onProbe(target);
  }

  const probeFailed = probeResult !== null && probeResult.outcome !== 'pass';

  return (
    <div
      data-testid="upstream-wizard-step-4"
      className="flex flex-col gap-5 rounded-xl bg-surface-container p-6"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-on-surface">
          {t('upstream_wizard.step.probe.heading', { name: upstreamName })}
        </h3>
        <p className="text-sm text-outline">{t('upstream_wizard.step.probe.body')}</p>
        <span className="font-mono text-[11px] text-outline-variant">{upstreamId}</span>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg bg-surface-container-low p-4">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('upstream_wizard.step.probe.target.label')}
          </span>
          <select
            value={selected}
            onChange={(e) => setSelected(e.currentTarget.value)}
            disabled={mappings.length === 0 || probing}
            data-testid="upstream-wizard-probe-select"
            className={INPUT_CLASS}
          >
            {mappings.map((a) => (
              <option
                key={`${a.vendor}/${a.model}/${a.protocol}`}
                value={`${a.vendor}/${a.model}/${a.protocol}`}
              >
                {a.vendor}/{a.model} ({a.protocol})
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => void handleProbeClick()}
          disabled={target === null || probing}
          data-testid="upstream-wizard-probe-submit"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
        >
          {probing
            ? t('upstream_wizard.step.probe.run.pending')
            : t('upstream_wizard.step.probe.run')}
        </button>
      </div>

      {probeResult !== null ? (
        <ProbeResultPanel result={probeResult} testIdPrefix="upstream-wizard-probe-result" />
      ) : null}

      {probeFailed ? (
        <p
          role="alert"
          data-testid="upstream-wizard-probe-warning"
          className="rounded-lg bg-error-container px-4 py-3 text-xs text-on-error-container"
        >
          {t('upstream_wizard.step.probe.warning.failed')}
        </p>
      ) : null}

      {error !== null ? (
        <p role="alert" data-testid="upstream-wizard-error" className="text-xs text-error">
          {error}
        </p>
      ) : null}

      <footer className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          disabled={finishing}
          data-testid="upstream-wizard-step-4-back"
          className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
        >
          {t('upstream_wizard.back')}
        </button>
        <button
          type="button"
          onClick={() => void onFinish()}
          disabled={finishing}
          data-testid="upstream-wizard-step-4-finish"
          className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
        >
          {finishing
            ? t('upstream_wizard.step.probe.finish.pending')
            : t('upstream_wizard.step.probe.finish')}
        </button>
      </footer>
    </div>
  );
}
